/**
 * Friends (privacy phase, Oct 22, 2026): signed-in players only, and never found by searching names.
 *
 * - Your friend code and invite link (daveseuchre.com/f/CODE): whoever enters it becomes your friend.
 * - "Add friend" on the stats of someone at your table sends a request they accept or decline.
 * - Shown inside your own "My stats"; the server sends a fresh list whenever it changes.
 */

import { type FriendStatus, type FriendsInfo, type ProfileInfo, REPORT_REASONS } from '@euchre/core';
import { h } from '../dom.js';
import { app, rerender, send, toast } from '../state.js';

export const friendsView = {
  data: null as FriendsInfo | null,
  /** A friend code from an /f/CODE link, used once this browser is signed in. */
  pendingCode: null as string | null,
};

/** daveseuchre.com/f/ABCD2345 opens the game and adds that friend. */
const linkMatch = new RegExp('^/f/([A-Za-z0-9]{8})/?$').exec(location.pathname);
if (linkMatch) {
  friendsView.pendingCode = linkMatch[1]!.toUpperCase();
  history.replaceState(null, '', '/');
}

export function friendsArrived(f: FriendsInfo): void {
  friendsView.data = f;
}

/** After the welcome: use a friend link if there is one. */
export function useFriendLink(signedIn: boolean): void {
  const code = friendsView.pendingCode;
  if (!code) return;
  if (!signedIn) {
    toast('To add your friend, use “Save my name” first. Then open the link again.', 9000);
    return;
  }
  friendsView.pendingCode = null;
  send({ type: 'friend_add_code', code });
}

export const inviteLink = (code: string) => `${location.origin}/f/${code}`;

async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied.');
  } catch {
    toast(`Your link: ${text}`, 10000);
  }
}

const btn = (text: string, onclick: () => void, cls = 'btn small') => h('button', { class: cls, type: 'button', text, onclick });

/** The Friends part of your own "My stats". */
export function friendsSection(): HTMLElement {
  const f = friendsView.data;
  if (!f) return h('section', { class: 'profile-block' }, h('h3', { text: 'Friends' }), h('p', { text: 'Loading…' }));
  const online = app.conn === 'open';
  const codeBox = h('input', {
    id: 'friend-code',
    class: 'field code-field',
    maxLength: 9,
    placeholder: 'Friend code',
    autocomplete: 'off',
    attrs: { 'aria-label': 'A friend’s code' },
  });
  const add = () => {
    const code = codeBox.value.trim();
    if (!code) return codeBox.focus();
    send({ type: 'friend_add_code', code });
    codeBox.value = '';
  };
  codeBox.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') add();
    ev.stopPropagation();
  });
  const incoming = f.friends.filter((x) => x.status === 'pending_in');
  const friends = f.friends.filter((x) => x.status === 'friends');
  const outgoing = f.friends.filter((x) => x.status === 'pending_out');
  const row = (name: string, extra: string, ...buttons: HTMLElement[]) =>
    h('li', { class: 'friend-row' }, h('span', { class: 'friend-name' }, name, extra ? h('span', { class: 'friend-extra', text: ` ${extra}` }) : null), h('span', { class: 'friend-actions' }, ...buttons));
  return h(
    'section',
    { class: 'profile-block friends' },
    h('h3', { text: 'Friends' }),
    h(
      'p',
      { class: 'friend-code-line' },
      'Your friend code: ',
      h('strong', { class: 'friend-code', text: f.code }),
      ' ',
      btn('Copy invite link', () => void copy(inviteLink(f.code))),
    ),
    h('p', { class: 'profile-note', text: 'Share your code or link only with people you know. Whoever enters it becomes your friend. Nobody can find you by searching your name.' }),
    h('div', { class: 'row friend-add' }, codeBox, h('button', { class: 'btn small primary', type: 'button', text: 'Add friend', disabled: !online, onclick: add })),
    incoming.length
      ? h('div', {}, h('h4', { text: 'Friend requests' }), h('ul', { class: 'friend-list' }, ...incoming.map((x) => row(x.name, '', btn('Accept', () => send({ type: 'friend_accept', accountId: x.accountId }), 'btn small primary'), btn('Decline', () => send({ type: 'friend_remove', accountId: x.accountId }))))))
      : null,
    friends.length
      ? h('ul', { class: 'friend-list' }, ...friends.map((x) => row(x.name, x.online ? '· online' : '', btn('Remove', () => send({ type: 'friend_remove', accountId: x.accountId }), 'btn small quiet'))))
      : h('p', { class: 'profile-empty', text: 'No friends yet.' }),
    outgoing.length
      ? h('div', {}, h('h4', { text: 'Waiting for them to accept' }), h('ul', { class: 'friend-list' }, ...outgoing.map((x) => row(x.name, '', btn('Cancel', () => send({ type: 'friend_remove', accountId: x.accountId }), 'btn small quiet')))))
      : null,
    blockedList(),
  );
}

/** On someone else's stats: where your friendship stands, with the one button that fits. */
export function friendButton(p: ProfileInfo): HTMLElement | null {
  const s: FriendStatus | undefined = p.friend;
  if (!s || !p.accountId) return null;
  const id = p.accountId;
  const after = () => setTimeout(() => send({ type: 'get_profile', playerId: p.playerId }), 300);
  const line = (text: string, ...buttons: HTMLElement[]) => h('p', { class: 'friend-status' }, text, ' ', ...buttons);
  if (s === 'friends') return line('You’re friends.', btn('Remove', () => (send({ type: 'friend_remove', accountId: id }), after()), 'btn small quiet'));
  if (s === 'pending_out') return line('Friend request sent.', btn('Cancel', () => (send({ type: 'friend_remove', accountId: id }), after()), 'btn small quiet'));
  if (s === 'pending_in') return line(`${p.name} wants to be friends.`, btn('Accept', () => (send({ type: 'friend_accept', accountId: id }), after()), 'btn small primary'));
  return line('', btn('Add friend', () => (send({ type: 'friend_request', playerId: p.playerId }), after()), 'btn small primary'));
}

export function friendsKey(): string {
  return JSON.stringify(friendsView.data);
}

/** Ask for the list again (e.g. when opening your own stats). */
export function refreshFriends(): void {
  send({ type: 'friends_get' });
  rerender();
}

// ---------------------------------------------------------------------------
// Block and Report (privacy phase): on every other player's stats, signed in or not.
// ---------------------------------------------------------------------------

const safety = { playerId: '', step: 'idle' as 'idle' | 'block' | 'report', reason: REPORT_REASONS[0] as string, note: '' };

export function safetyKey(): string {
  return JSON.stringify([safety.playerId, safety.step, safety.reason]);
}

export function safetyControls(p: ProfileInfo): HTMLElement {
  if (safety.playerId !== p.playerId) Object.assign(safety, { playerId: p.playerId, step: 'idle', reason: REPORT_REASONS[0], note: '' });
  const go = (step: typeof safety.step) => {
    safety.step = step;
    rerender();
  };
  if (safety.step === 'block') {
    return h(
      'div',
      { class: 'safety confirm' },
      h('p', { text: `Block ${p.name}? You won’t see each other’s chat, you won’t be friends, and they can’t join tables you host. They aren’t told.` }),
      h('div', { class: 'friend-actions' },
        btn(`Block ${p.name}`, () => {
          send({ type: 'block', playerId: p.playerId });
          go('idle');
        }, 'btn small danger'),
        btn('Cancel', () => go('idle'), 'btn small quiet')),
    );
  }
  if (safety.step === 'report') {
    const note = h('textarea', { attrs: { rows: '2', maxlength: '500', placeholder: 'Anything else Dave should know? (optional)', 'aria-label': 'Report details' } }) as HTMLTextAreaElement;
    note.value = safety.note;
    note.addEventListener('input', () => (safety.note = note.value));
    note.addEventListener('keydown', (ev) => ev.stopPropagation());
    return h(
      'div',
      { class: 'safety confirm' },
      h('p', { text: `Report ${p.name} to the game’s admin. Their name, this table and what they said here are sent with your report.` }),
      h('fieldset', { class: 'profile-privacy' },
        h('legend', { text: 'What happened?' }),
        ...REPORT_REASONS.map((r) =>
          h('label', { class: 'profile-choice' },
            h('input', { type: 'radio', name: 'report-reason', value: r, checked: safety.reason === r, onchange: () => (safety.reason = r) }),
            h('span', { text: r })))),
      note,
      h('div', { class: 'friend-actions' },
        btn('Send report', () => {
          send({ type: 'report', playerId: p.playerId, reason: safety.reason, ...(safety.note.trim() ? { note: safety.note.trim() } : {}) });
          safety.note = '';
          go('idle');
        }, 'btn small primary'),
        btn('Cancel', () => go('idle'), 'btn small quiet')),
    );
  }
  return h('p', { class: 'safety' }, btn('Block', () => go('block'), 'link-btn'), ' · ', btn('Report', () => go('report'), 'link-btn'));
}

/** People you've blocked, with Unblock (inside your own Friends section). */
export function blockedList(): HTMLElement | null {
  const list = friendsView.data?.blocked ?? [];
  if (!list.length) return null;
  return h('div', {},
    h('h4', { text: 'Blocked' }),
    h('ul', { class: 'friend-list' }, ...list.map((b) =>
      h('li', { class: 'friend-row' }, h('span', { class: 'friend-name', text: b.name }), h('span', { class: 'friend-actions' }, btn('Unblock', () => send({ type: 'unblock', id: b.id }), 'btn small quiet'))))));
}

// ---------------------------------------------------------------------------
// Your data (privacy phase): download it, or delete your account. Inside your own "My stats".
// ---------------------------------------------------------------------------

const yours = { confirmDelete: false };

export function yourDataKey(): string {
  return String(yours.confirmDelete);
}

/** The server sent your data: save it as a file. */
export function saveMyData(data: Record<string, unknown>): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = h('a', { attrs: { href: url, download: 'daves-euchre-my-data.json' } });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  toast('Your data was downloaded.');
}

export function yourDataSection(): HTMLElement {
  const go = (v: boolean) => {
    yours.confirmDelete = v;
    rerender();
  };
  return h(
    'section',
    { class: 'profile-block your-data' },
    h('h3', { text: 'Your data' }),
    h('p', { class: 'profile-note' }, 'What we keep and why: ', h('a', { attrs: { href: '/privacy', target: '_blank', rel: 'noopener' }, text: 'Privacy' }), '.'),
    yours.confirmDelete
      ? h(
          'div',
          { class: 'safety confirm' },
          h('p', { text: 'Delete your account for good? Your email, saved name, friends and blocks are removed now. Past games keep their scores but show “Former player”. You can keep playing as a guest.' }),
          h('div', { class: 'friend-actions' },
            btn('Yes, delete my account', () => {
              send({ type: 'delete_account', confirm: 'DELETE' });
              go(false);
            }, 'btn small danger'),
            btn('Keep my account', () => go(false), 'btn small quiet')),
        )
      : h('p', { class: 'friend-actions' },
          btn('Download my data', () => send({ type: 'download_data' })),
          btn('Delete my account', () => go(true), 'btn small quiet')),
  );
}
